import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import type { CanvasserLocationDto, ContactCreatedEvent } from '@signalvote/shared';
import { API_BASE_URL } from '../config';

export type { ContactCreatedEvent };

export interface UseSignalROptions {
  campaignId: string | null;
  subsidiaryCode: string | null;
  enabled: boolean;
  onContactCreated?: (event: ContactCreatedEvent) => void;
}

/**
 * `connecting`  – initial handshake in flight (before JoinCampaign resolves)
 * `live`        – connected and joined; also the default when the hook is disabled
 *                 (nothing to be unhealthy about)
 * `reconnecting`– transport dropped, SignalR is retrying
 * `offline`     – gave up (retries exhausted or initial start() rejected)
 */
export type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'offline';

export interface UseSignalRReturn {
  positions: GeoJSON.FeatureCollection;
  activeCount: number;
  isConnected: boolean;
  connectionState: ConnectionState;
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const LIVE_COLOR = '#22C55E';
const STALE_COLOR = '#9CA3AF';

function toFeature(dto: CanvasserLocationDto): GeoJSON.Feature {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [dto.longitude, dto.latitude] },
    properties: {
      volunteerId: dto.volunteerId,
      volunteerName: dto.volunteerName,
      volunteerInitials: dto.volunteerInitials,
      heading: dto.heading ?? null,
      speed: dto.speed ?? null,
      shiftId: dto.shiftId,
      isStale: dto.isStale,
      color: dto.isStale ? STALE_COLOR : LIVE_COLOR,
      lastUpdated: dto.lastUpdated,
    },
  };
}

export function useSignalR({ campaignId, subsidiaryCode, enabled, onContactCreated }: UseSignalROptions): UseSignalRReturn {
  const [positions, setPositions] = useState<GeoJSON.FeatureCollection>(EMPTY_FC);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('live');
  const connectionRef = useRef<HubConnection | null>(null);
  const mapRef = useRef<Map<string, GeoJSON.Feature>>(new Map());
  const activeKeyRef = useRef<string | null>(null);
  const onContactCreatedRef = useRef(onContactCreated);
  onContactCreatedRef.current = onContactCreated;

  const rebuild = useCallback((): GeoJSON.FeatureCollection => ({
    type: 'FeatureCollection',
    features: Array.from(mapRef.current.values()),
  }), []);

  useEffect(() => {
    if (!enabled || !campaignId || !subsidiaryCode) {
      connectionRef.current?.stop();
      connectionRef.current = null;
      mapRef.current.clear();
      setPositions(EMPTY_FC);
      setIsConnected(false);
      setConnectionState('live');
      activeKeyRef.current = null;
      return;
    }

    const key = `${subsidiaryCode}:${campaignId}`;
    activeKeyRef.current = key;
    const token = localStorage.getItem('auth_token') ?? '';

    setConnectionState('connecting');

    const conn = new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hubs/campaign?subsidiary=${subsidiaryCode}&access_token=${token}`)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = conn;
    mapRef.current.clear();

    conn.on('ReceiveLocationUpdate', (dto: CanvasserLocationDto) => {
      if (activeKeyRef.current !== key) return;
      mapRef.current.set(dto.volunteerId, toFeature(dto));
      setPositions(rebuild());
    });

    conn.on('CanvasserStale', (volunteerId: string) => {
      if (activeKeyRef.current !== key) return;
      const f = mapRef.current.get(volunteerId);
      if (f?.properties) {
        f.properties.isStale = true;
        f.properties.color = STALE_COLOR;
        mapRef.current.set(volunteerId, { ...f });
        setPositions(rebuild());
      }
    });

    conn.on('CanvasserStopped', (volunteerId: string) => {
      if (activeKeyRef.current !== key) return;
      mapRef.current.delete(volunteerId);
      setPositions(rebuild());
    });

    conn.on('ContactCreated', (event: ContactCreatedEvent) => {
      if (activeKeyRef.current !== key) return;
      onContactCreatedRef.current?.(event);
    });

    conn.onreconnecting(() => {
      if (activeKeyRef.current !== key) return;
      setIsConnected(false);
      setConnectionState('reconnecting');
    });

    conn.onreconnected(async () => {
      if (activeKeyRef.current !== key) return;
      try {
        const snapshot = await conn.invoke<CanvasserLocationDto[]>('JoinCampaign', campaignId);
        mapRef.current.clear();
        for (const dto of snapshot) mapRef.current.set(dto.volunteerId, toFeature(dto));
        setPositions(rebuild());
        setIsConnected(true);
        setConnectionState('live');
      } catch (err) {
        console.error('Reconnect JoinCampaign failed:', err);
        setConnectionState('offline');
      }
    });

    conn.onclose(() => {
      if (activeKeyRef.current !== key) return;
      setIsConnected(false);
      setConnectionState('offline');
    });

    (async () => {
      try {
        await conn.start();
        if (activeKeyRef.current !== key) { conn.stop(); return; }
        const snapshot = await conn.invoke<CanvasserLocationDto[]>('JoinCampaign', campaignId);
        for (const dto of snapshot) mapRef.current.set(dto.volunteerId, toFeature(dto));
        setPositions(rebuild());
        setIsConnected(true);
        setConnectionState('live');
      } catch (err) {
        console.error('SignalR connection failed:', err);
        if (activeKeyRef.current === key) setConnectionState('offline');
      }
    })();

    return () => {
      activeKeyRef.current = null;
      conn.stop();
      connectionRef.current = null;
      mapRef.current.clear();
      setPositions(EMPTY_FC);
      setIsConnected(false);
      setConnectionState('live');
    };
  }, [campaignId, subsidiaryCode, enabled, rebuild]);

  const activeCount = useMemo(
    () => positions.features.filter(f => f.properties && !f.properties.isStale).length,
    [positions]
  );

  return { positions, activeCount, isConnected, connectionState };
}

export default useSignalR;
